/**
 * Site Refresh 2026: homepage slideshow (A15). Markup: snippets/refresh-slideshow.liquid.
 *
 * Autoplay (data-rs-autoplay ms, 0 = off) with a fade and loop. The active tab's progress line
 * fills with the timer. The timer stops while the Pause button is pressed, while the pointer is
 * over the slideshow, while anything in it has keyboard focus, when it is off-screen or the tab is
 * hidden; with prefers-reduced-motion it never starts on its own (Play starts it).
 * Tabs: roving tabindex, arrow keys / Home / End switch slides. Touch: swipe left / right.
 * Slides 2+ load their image (data-srcset) shortly after the page has loaded, and in any case
 * before they are shown.
 */
(() => {
	if (window.__rsInit) return;
	window.__rsInit = true;

	const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');

	function loadImage(slide) {
		const pic = slide && slide.querySelector('.js-rs-lazy');
		if (!pic) return;
		pic.classList.remove('js-rs-lazy');
		pic.querySelectorAll('[data-srcset]').forEach((el) => {
			el.setAttribute('srcset', el.getAttribute('data-srcset'));
			el.removeAttribute('data-srcset');
		});
	}

	function init(root) {
		if (root.__rs) return;
		root.__rs = true;

		const slides = [...root.querySelectorAll('.js-rs-slide')];
		const tabs = [...root.querySelectorAll('.js-rs-tab')];
		const bars = tabs.map((t) => t.querySelector('.js-rs-progress'));
		const pauseBtn = root.querySelector('.js-rs-pause');
		const delay = parseInt(root.getAttribute('data-rs-autoplay'), 10) || 0;
		const loop = root.getAttribute('data-rs-loop') !== 'false';
		const labels = { pause: root.getAttribute('data-rs-pause'), play: root.getAttribute('data-rs-play') };
		if (slides.length < 2) return;

		let index = 0;
		let elapsed = 0;
		let last = 0;
		let raf = 0;
		let paused = reduced.matches;
		let hover = false;
		let focus = false;
		let visible = true;

		function running() {
			return delay > 0 && !paused && !hover && !focus && visible && !document.hidden;
		}

		function paint() {
			bars.forEach((bar, i) => {
				if (!bar) return;
				let p = 0;
				if (i === index) p = delay > 0 ? Math.min(elapsed / delay, 1) : 1;
				bar.style.transform = `scaleX(${p})`;
			});
		}

		function go(next, { focusTab = false } = {}) {
			const n = slides.length;
			if (!loop && (next < 0 || next >= n)) return;
			next = ((next % n) + n) % n;
			loadImage(slides[next]);
			if (next !== index) {
				slides[index].classList.remove('is-active');
				slides[index].setAttribute('inert', '');
				tabs[index].classList.remove('is-active');
				tabs[index].setAttribute('aria-selected', 'false');
				tabs[index].setAttribute('tabindex', '-1');
				index = next;
				slides[index].classList.add('is-active');
				slides[index].removeAttribute('inert');
				tabs[index].classList.add('is-active');
				tabs[index].setAttribute('aria-selected', 'true');
				tabs[index].setAttribute('tabindex', '0');
			}
			if (focusTab) tabs[index].focus();
			elapsed = 0;
			paint();
			tick();
		}

		function frame(now) {
			raf = 0;
			if (!running()) { last = 0; return; }
			const dt = last ? Math.min(now - last, 100) : 0;
			last = now;
			elapsed += dt;
			// warm the next slide's image before its turn
			if (elapsed > delay * 0.6) loadImage(slides[(index + 1) % slides.length]);
			if (elapsed >= delay) {
				if (!loop && index === slides.length - 1) { elapsed = delay; paint(); return; }
				go(index + 1);
				return;
			}
			paint();
			raf = requestAnimationFrame(frame);
		}

		function tick() {
			if (running()) {
				if (!raf) { last = 0; raf = requestAnimationFrame(frame); }
			} else {
				if (raf) cancelAnimationFrame(raf);
				raf = 0;
				last = 0;
			}
		}

		function setPaused(value) {
			paused = value;
			if (pauseBtn) {
				pauseBtn.setAttribute('aria-pressed', paused ? 'true' : 'false');
				pauseBtn.setAttribute('aria-label', paused ? labels.play : labels.pause);
			}
			tick();
		}

		tabs.forEach((tab, i) => {
			tab.addEventListener('click', () => go(i));
		});

		const tablist = root.querySelector('.js-rs-tabs');
		if (tablist) {
			tablist.addEventListener('keydown', (e) => {
				const n = tabs.length;
				let target = null;
				if (e.key === 'ArrowRight' || e.key === 'ArrowDown') target = (index + 1) % n;
				else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') target = (index - 1 + n) % n;
				else if (e.key === 'Home') target = 0;
				else if (e.key === 'End') target = n - 1;
				if (target === null) return;
				e.preventDefault();
				go(target, { focusTab: true });
			});
		}

		if (pauseBtn) {
			pauseBtn.addEventListener('click', () => setPaused(!paused));
		}

		root.addEventListener('pointerenter', (e) => { if (e.pointerType === 'mouse') { hover = true; tick(); } });
		root.addEventListener('pointerleave', (e) => { if (e.pointerType === 'mouse') { hover = false; tick(); } });
		root.addEventListener('focusin', (e) => {
			if (e.target.matches(':focus-visible')) { focus = true; tick(); }
		});
		root.addEventListener('focusout', (e) => {
			if (!root.contains(e.relatedTarget)) { focus = false; tick(); }
		});

		// swipe
		const stage = root.querySelector('.js-rs-stage');
		let startX = null;
		let startY = 0;
		stage.addEventListener('touchstart', (e) => {
			if (e.touches.length !== 1) return;
			startX = e.touches[0].clientX;
			startY = e.touches[0].clientY;
		}, { passive: true });
		stage.addEventListener('touchend', (e) => {
			if (startX === null) return;
			const dx = e.changedTouches[0].clientX - startX;
			const dy = e.changedTouches[0].clientY - startY;
			startX = null;
			if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.5) go(index + (dx < 0 ? 1 : -1));
		}, { passive: true });

		if ('IntersectionObserver' in window) {
			new IntersectionObserver((entries) => {
				visible = entries[0].isIntersecting;
				tick();
			}).observe(root);
		}
		document.addEventListener('visibilitychange', tick);
		reduced.addEventListener('change', () => setPaused(reduced.matches || paused));

		// load the next slide's image once the page is idle
		const warm = () => loadImage(slides[1]);
		const idle = window.requestIdleCallback || ((fn) => setTimeout(fn, 1500));
		if (document.readyState === 'complete') idle(warm);
		else window.addEventListener('load', () => idle(warm), { once: true });

		setPaused(paused);
		paint();
	}

	function initAll() {
		document.querySelectorAll('[data-rs]').forEach(init);
	}

	if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initAll);
	else initAll();
	document.addEventListener('shopify:section:load', initAll);
})();
